import { useEffect, useState } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import LoginPage from "@/pages/admin/auth/LoginPage";
import MFAVerifyPage from "@/pages/admin/auth/MFAVerifyPage";

interface Props {
  children: React.ReactNode;
}

type LocalStep = "loading" | "login" | "mfa-verify" | "authenticated";

export default function AdminGuard({ children }: Props) {
  const [localStep, setLocalStep] = useState<LocalStep>("loading");
  const { sessionLoading, logout, cancelPendingAuth, resolveAdminStep } = useAdminAuth();

  useEffect(() => {
    if (sessionLoading) {
      return;
    }

    let active = true;

    void resolveAdminStep().then((result) => {
      if (!active) return;
      setLocalStep(result.step);
    });

    return () => {
      active = false;
    };
  }, [resolveAdminStep, sessionLoading]);

  const handleAuthenticated = () => {
    setLocalStep("authenticated");
  };

  const handleBackToLogin = async () => {
    await cancelPendingAuth();
    setLocalStep("login");
  };

  if (localStep === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-slate-600 shadow-sm">
          <i className="ri-loader-4-line animate-spin text-lg text-orange-500"></i>
          <span className="text-sm font-medium">Verificando acesso seguro...</span>
        </div>
      </div>
    );
  }

  if (localStep === "login") {
    return <LoginPage onLoginSuccess={() => setLocalStep("mfa-verify")} />;
  }

  if (localStep === "mfa-verify") {
    return (
      <MFAVerifyPage
        onVerified={handleAuthenticated}
        onBack={() => {
          void handleBackToLogin();
        }}
      />
    );
  }

  return (
    <div className="relative">
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={() => {
            void logout().then(() => {
              setLocalStep("login");
            });
          }}
          className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 hover:text-red-500 hover:border-red-200 text-xs font-medium px-3 py-2 rounded-xl shadow-sm transition cursor-pointer whitespace-nowrap"
        >
          <i className="ri-logout-box-r-line"></i>
          Sair
        </button>
      </div>
      {children}
    </div>
  );
}
